import React from "react";
import Stack from "./Stack";
import PropTypes from "prop-types";

/**
 * VStack (Vertical Stack) - A Stack component that always arranges children vertically.
 * 
 * This is a convenience wrapper around Stack with direction="column" by default.
 * Useful for creating vertical layouts with consistent spacing.
 * 
 * @component
 * @example
 * <VStack spacing={2}>
 *   <TextField />
 *   <TextField />
 *   <Button>Submit</Button>
 * </VStack>
 * 
 * @example
 * <VStack spacing={3} alignItems="center">
 *   <Typography variant="h1">Title</Typography>
 *   <Typography variant="body1">Description</Typography>
 * </VStack>
 */
const VStack = React.forwardRef(({ children, direction, ...props }, ref) => {
  // Always force column direction for VStack
  return (
    <Stack ref={ref} direction="column" {...props}>
      {children}
    </Stack>
  );
});

VStack.displayName = 'VStack';

VStack.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * Defines the space between immediate children
   * Can be a number (theme spacing multiplier) or object for responsive values
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      xl: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ]),
  /**
   * Defines the align-items style property
   */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "stretch",
      "baseline",
    ]),
    PropTypes.object,
  ]),
  /**
   * Defines the justify-content style property
   */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "space-between",
      "space-around",
      "space-evenly",
    ]),
    PropTypes.object,
  ]),
  /**
   * If true, the CSS flexbox gap is used instead of applying margin to children
   */
  useFlexGap: PropTypes.bool,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - inherit from underlying Stack/MUI behavior

export default VStack;

